import { useCallback, useContext, useEffect, useState } from 'react';
import { MyContext } from '../context';
import {
	getStore,
	getStoreDefaults,
	getStorePaceAccount,
	getStoreEmailSettings,
	listShippingMethodMappings,
	listStorePaymentMethods,
	listStoreLoyaltyPrograms,
	listPaymentTermMappings,
	listProducts,
} from '../utils/queries';

const emptyState = {
	loading: true,
	activeStoreId: null,
	store: null,
	usesPace: false,
	paceAccount: null,
	defaults: null,
	logoUrl: null,
	shippingMappings: [],
	paymentMethodRows: [],
	loyaltyProgramRows: [],
	paymentTermMappingRows: [],
	productsTotal: 0,
	sections: [],
	allDone: false,
};

// Shared "is this store ready to leave setup" check -- used by both the
// Finish Setup page itself and layout.js's redirect-until-configured guard,
// so they can't drift out of sync on what "done" means. Going Live is
// deliberately not one of these -- it's a separate action the admin can take
// whenever, not a gate on navigating the rest of the app.
export default function useStoreSetupStatus() {
	const { activeStoreId } = useContext(MyContext);
	const [state, setState] = useState(emptyState);

	const refresh = useCallback(async () => {
		if (!activeStoreId) {
			// Not "still loading" -- there's genuinely no store to check, so
			// nothing blocks navigation (layout.js's redirect guard) and
			// StoreSetup can render its "No store selected" state immediately.
			const next = { ...emptyState, loading: false, allDone: true };
			setState(next);
			return next;
		}
		setState((s) => ({ ...s, loading: true }));

		const [
			storeResult,
			defaultsResult,
			paceAccountResult,
			emailSettingsResult,
			shippingResult,
			paymentMethodsResult,
			loyaltyProgramsResult,
			paymentTermMappingsResult,
			productsResult,
		] = await Promise.all([
			getStore(activeStoreId),
			getStoreDefaults(activeStoreId),
			getStorePaceAccount(activeStoreId),
			getStoreEmailSettings(activeStoreId),
			listShippingMethodMappings(activeStoreId),
			listStorePaymentMethods(activeStoreId),
			listStoreLoyaltyPrograms(activeStoreId),
			listPaymentTermMappings(activeStoreId),
			listProducts({ storeId: activeStoreId, limit: 1 }),
		]);

		const store = storeResult?.success ? storeResult.data : null;
		const usesPace = Boolean(store?.erpCustomerNumber);
		const paceAccount = paceAccountResult?.success ? paceAccountResult.data : null;
		const shippingMappings = shippingResult?.success ? shippingResult.data : [];
		const paymentMethodRows = paymentMethodsResult?.success ? paymentMethodsResult.data : [];
		const loyaltyProgramRows = loyaltyProgramsResult?.success ? loyaltyProgramsResult.data : [];
		const paymentTermMappingRows = paymentTermMappingsResult?.success ? paymentTermMappingsResult.data : [];
		const productsTotal = productsResult?.success ? productsResult.data.pagination.total : 0;
		const logoUrl = emailSettingsResult?.success ? emailSettingsResult.data?.logoUrl : null;

		const identityDone = Boolean(logoUrl);
		const productsDone = productsTotal > 0;
		const shippingDone = shippingMappings.length > 0;
		// A store can accept Loyalty Points redemption, Invoice (ERP payment
		// terms), or a Credit Card/PO payment method without any of the
		// others configured -- any one of them counts as "payments" being set up.
		const paymentsDone = paymentMethodRows.length > 0 || loyaltyProgramRows.length > 0 || paymentTermMappingRows.length > 0;
		const paceDone = Boolean(paceAccount?.paceCustomerNumber);

		const sections = [
			usesPace
				? {
						key: 'pace',
						label: 'ERP details',
						description: "Confirm the ERP defaults pulled from this store's ERP customer record.",
						done: paceDone,
					}
				: null,
			{
				key: 'identity',
				label: 'Store identity',
				description: 'Add your store name and logo so customers recognize your brand.',
				done: identityDone,
			},
			{
				key: 'products',
				label: 'Products',
				description: 'Add at least one product so customers have something to buy.',
				done: productsDone,
			},
			// Shipping method mappings require a ERP ship-via id (schema
			// enforces erpShipViaId NOT NULL) -- a standalone store has no way
			// to ever satisfy this, so it isn't part of its checklist.
			usesPace
				? {
						key: 'shipping',
						label: 'Shipping methods',
						description: 'Choose which ERP shipping carriers this store offers at checkout.',
						done: shippingDone,
					}
				: null,
			{
				key: 'payments',
				label: 'Payments',
				description: 'Connect a payment method so customers can check out.',
				done: paymentsDone,
			},
		].filter(Boolean);

		const next = {
			loading: false,
			activeStoreId,
			store,
			usesPace,
			paceAccount,
			defaults: defaultsResult?.success ? defaultsResult.data : null,
			logoUrl,
			shippingMappings,
			paymentMethodRows,
			loyaltyProgramRows,
			paymentTermMappingRows,
			productsTotal,
			sections,
			allDone: sections.every((s) => s.done),
		};
		setState(next);
		// Returned so callers that need the *current* answer right now (e.g. the
		// sidebar's Home link, whose cached `allDone` only refreshes when the
		// active store itself changes, not when a setup task elsewhere on the
		// same store gets completed) can await a fresh check instead of trusting
		// whatever this hook last happened to fetch.
		return next;
	}, [activeStoreId]);

	useEffect(() => {
		refresh();
	}, [refresh]);

	return { ...state, refresh };
}
